import { DeductionExt } from "@/api"
import { build_request_backup_form, submit_dispute } from "@/api/dispute"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"
import { toastResult } from "@/toast"
import { Err, Ok, Result } from "@/utils"
import { useEffect, useState } from "preact/hooks"

const inFlight = new Set<string>()
const listeners = new Set<() => void>()

function notifyListeners() {
  for (const listener of listeners) {
    listener()
  }
}

export function useBackupRequestInFlight(deductionId: string): boolean {
  const [, setTick] = useState(0)
  useEffect(() => {
    function onChange() {
      setTick(t => t + 1)
    }
    listeners.add(onChange)
    return () => {
      listeners.delete(onChange)
    }
  }, [])
  return inFlight.has(deductionId)
}

/** Most backup requests one bulk action may send. Each one is a message into the customer's inbox. */
export const MAX_BULK_BACKUP_REQUESTS = 10

/** The reason a bulk backup request for `count` deductions is refused, or `null` when it may go. */
export function bulk_backup_request_limit_error(count: number): string | null {
  if (count <= MAX_BULK_BACKUP_REQUESTS) {
    return null
  }
  return `You selected ${count} deductions that need backup. Please request backup for at most ${MAX_BULK_BACKUP_REQUESTS} at a time: each request is a separate message to the customer, and we don't want to overwhelm their systems.`
}

export interface RequestBackupOptions {
  is_rerequest?: boolean
}

export async function requestBackup(
  deduction: DeductionExt,
  options: RequestBackupOptions = {},
): Promise<Result<void, Error>> {
  if (inFlight.has(deduction.id)) {
    return Ok(undefined)
  }
  inFlight.add(deduction.id)
  notifyListeners()
  try {
    let form = build_request_backup_form(deduction, options)
    let res = await submit_dispute(form)
    toastResult(res, "Backup requested successfully", "An error occurred while requesting backup")
    if (res.ok) {
      triggerRefreshFlag()
    }
    return res.ok ? Ok(undefined) : Err(res.error)
  } finally {
    inFlight.delete(deduction.id)
    notifyListeners()
  }
}

if (import.meta.vitest) {
  const { describe, it, expect, beforeEach } = import.meta.vitest

  describe("bulk_backup_request_limit_error", () => {
    it("allows up to the limit", () => {
      expect(bulk_backup_request_limit_error(1)).toBeNull()
      expect(bulk_backup_request_limit_error(MAX_BULK_BACKUP_REQUESTS)).toBeNull()
    })

    it("refuses more than the limit and says why", () => {
      let error = bulk_backup_request_limit_error(MAX_BULK_BACKUP_REQUESTS + 1)
      expect(error).toContain("11 deductions")
      expect(error).toContain("at most 10 at a time")
      expect(error).toContain("overwhelm their systems")
    })
  })

  describe("requestBackup in-flight guard", () => {
    beforeEach(() => {
      inFlight.clear()
      listeners.clear()
    })

    it("tracks in-flight deduction ids", () => {
      expect(inFlight.has("ded-1")).toBe(false)
      inFlight.add("ded-1")
      expect(inFlight.has("ded-1")).toBe(true)
      inFlight.delete("ded-1")
      expect(inFlight.has("ded-1")).toBe(false)
    })
  })
}
